/* eslint-disable max-lines */

import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  InjectionToken,
  SimpleChanges,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  output,
  inject,
  DestroyRef,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { deepEqual } from 'fast-equals';
import type { Observable } from 'rxjs';
import { animationFrameScheduler, BehaviorSubject, defer, fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { StageCategoryColorIndicatorComponent } from '@core/offers-ui';
import {
  EMPTY_VALUE,
  FilterGroup,
  injectReportVisualizationOptions,
  NONE_VALUE,
} from '@core/reports-types';
import type {
  VisualizationData,
  Dimension,
  ReportsDataTableRow,
  TableData,
  TableDataValueRow,
  ReportDashboardComponentViewData,
} from '@core/reports-types';
import type { DataTableColumn } from '@core/user-interface-legacy';
import {
  DataTableComponent,
  IconComponent,
  PaginationComponent,
  PaginationCounterComponent,
  ResizeObserverEntryDirective,
  ToolbarComponent,
  ToolbarSecondaryComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

import { TablePaginationPipe } from './table-pagination/table-pagination.pipe';

export type Pagination = {
  pagesCount: number;
  limit: number;
  page: number;
  start: number;
  end: number;
  total: number;
};

const tableColWidth = 200;

const insert = <T>(item: T, collection: T[], index: number): T[] => {
  const head = collection.slice(0, index);
  const tail = collection.slice(index);
  return [...head, item, ...tail];
};

export const disablePaginationHeightLimiting = new InjectionToken<boolean>(
  'disablePaginationHeightLimiting',
);

@Component({
  selector: 'reports-dashboards-table',
  templateUrl: './table.component.html',
  styleUrls: ['./shared-table-style.less', './table.component.less'],
  imports: [
    IconComponent,
    AsyncPipe,
    DataTableComponent,
    ToolbarComponent,
    ToolbarSecondaryComponent,
    PaginationCounterComponent,
    PaginationComponent,
    NgTemplateOutlet,
    TablePaginationPipe,
    TruncateComponent,
    StageCategoryColorIndicatorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.export]': 'isExport()',
  },
})
export class ReportDashboardsTableComponent {
  private readonly resizeObserverEntry = inject(ResizeObserverEntryDirective);
  private readonly elRef = inject(ElementRef);
  private readonly disablePaginationHeightLimiting = inject(disablePaginationHeightLimiting, {
    optional: true,
  });
  private readonly destroyRef = inject(DestroyRef);
  private readonly options = injectReportVisualizationOptions({
    optional: true,
  });

  protected readonly cdRef = inject(ChangeDetectorRef);

  public readonly vcRef = inject(ViewContainerRef);
  public readonly i18n = inject(I18nService);
  public readonly INITIAL_LIMIT: number = 4;

  @Input() public set data(data: VisualizationData<TableData>) {
    if (!data.dataset.isInitial) this._data = data;
  }

  public get data(): VisualizationData<TableData> {
    return this._data;
  }

  public readonly component = input<ReportDashboardComponentViewData>(undefined!);
  public readonly resourcesPreviewAvailable = input<boolean>(undefined!);
  public readonly paginate = input(true);
  public readonly isExport = input(false);

  public openResourceAt = output<[number, number]>();
  public tableSortChange = output<DataTableColumn>();

  public dataTableColumnsMapped: DataTableColumn[] = [];
  public dataTableRowsMapped: ReportsDataTableRow[] = [];
  public pagination$: Observable<Pagination>;
  public shadows$: Observable<{ left: boolean; right: boolean }>;
  public tableShadowHidden: boolean = false;
  public headerCell: TemplateRef<any>;

  @ViewChild('dataTableComponent', { read: ElementRef }) public dataTableComponent: ElementRef;
  @ViewChild('toolbarComponent', { read: ElementRef }) public toolbarComponent: ElementRef;
  @ViewChild('scrollContainer', { read: ElementRef }) public scrollContainer: ElementRef;

  @ViewChild('headerCellStageCategory', { read: TemplateRef, static: true })
  public headerCellStageCategory: TemplateRef<any>;
  @ViewChild('headerCellDefault', { read: TemplateRef, static: true })
  public headerCellDefault: TemplateRef<any>;

  @ViewChild('cellTemplateStage', { read: TemplateRef }) public cellTemplateStage: TemplateRef<any>;

  private _data: VisualizationData<TableData>;

  private paginationSubject: BehaviorSubject<Pagination> = new BehaviorSubject({
    page: 1,
    limit: this.INITIAL_LIMIT,
    pagesCount: 0,
    start: 0,
    end: 0,
    total: 0,
  });

  private shadowsSubject: BehaviorSubject<{ left: boolean; right: boolean }> = new BehaviorSubject<{
    left: boolean;
    right: boolean;
  }>({
    left: false,
    right: false,
  });

  private columnsCountChanged = new Subject<void>();

  public ngOnInit() {
    this.enablePagination();

    this.shadows$ = this.shadowsSubject
      .asObservable()
      .pipe(distinctUntilChanged(deepEqual), takeUntilDestroyed(this.destroyRef));
    this.pagination$ = this.paginationSubject
      .asObservable()
      .pipe(distinctUntilChanged(deepEqual), takeUntilDestroyed(this.destroyRef));
  }

  public ngAfterViewInit() {
    this.enableScrollShadow();
  }

  public ngOnDestroy() {
    this.shadowsSubject.complete();
    this.paginationSubject.complete();
  }

  public enablePagination() {
    if (!this.paginate()) {
      this.setPagination(this.data.vis.values.length);
      return;
    }

    const estimatedRowHeight = 45;
    const estimatedBottomSpace = 90;

    const height$ = this.resizeObserverEntry.resized.pipe(
      map(({ height }) => height),
      startWith(<number>this.elRef.nativeElement.offsetHeight),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    );

    height$.subscribe(height => {
      const rows = Math.round((height - estimatedBottomSpace) / estimatedRowHeight);
      const limit = !!this.disablePaginationHeightLimiting ? 9999 : rows - 1; // subtract header row
      this.setPagination(limit);
    });
  }

  public enableScrollShadow() {
    if (this.isExport()) {
      this.shadowsSubject.next({ left: false, right: false });
      return;
    }
    const width$ = this.resizeObserverEntry.resized.pipe(
      map(({ width }) => width),
      // any emmision will do
      startWith(this.resizeObserverEntry.entry?.width),
      distinctUntilChanged(),
    );

    const scroll$ = defer(() => fromEvent(this.scrollContainer.nativeElement, 'scroll'));

    merge(width$, scroll$)
      .pipe(
        debounceTime(1, animationFrameScheduler),
        map(() => this.scrollContainer?.nativeElement),
        filterBoolean,
        map(scrollContainer => {
          const { scrollWidth = 0, scrollLeft = 0, offsetWidth = 0 } = scrollContainer;
          const left = scrollLeft > 0;
          const right = scrollLeft + offsetWidth < scrollWidth;
          return { left, right };
        }),
        distinctUntilChanged(deepEqual),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(data => {
        this.shadowsSubject.next(data);
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    const component = this.component();
    if (this.data) {
      const prevColumnsCount = this.dataTableColumnsMapped.length;
      const nextColumnsCount = this.data.vis.dimensions.length;

      if (prevColumnsCount && prevColumnsCount !== nextColumnsCount) {
        this.columnsCountChanged.next();
      }

      if (component) {
        this.headerCell =
          component?.secondaryGroup === FilterGroup.StageCategory
            ? this.headerCellStageCategory
            : this.headerCellDefault;

        this.dataTableColumnsMapped = this.data.vis.dimensions.map(
          ({ id, name }: Dimension, index) => ({
            index,
            id: `${id || name}`,
            label: name,
            geometry: {
              width: tableColWidth,
              minWidth: tableColWidth,
            },
            sort: this.determineColumnSort(id),
            options: {
              draggable: false,
              resizable: false,
              removable: false,
              sortable: this.options?.tableSortingAvailable ?? false,
            },
          }),
        );

        this.dataTableRowsMapped = this.data.vis.values.map((row, index) => ({
          index,
          values: this.valuesReduce(row),
          id: generateUuid(),
        }));

        if (Array.isArray(this.data.vis.additionalColumns)) {
          this.data.vis.additionalColumns.forEach(({ column, dataFactory }) => {
            this.dataTableColumnsMapped = insert(
              column,
              this.dataTableColumnsMapped,
              column.index || 0,
            );

            this.dataTableRowsMapped = this.dataTableRowsMapped.map(dataFactory);
          });
        }

        if (
          changes.data?.currentValue?.dataset.datasetSize.rows !==
          changes.data?.previousValue?.dataset.datasetSize.rows
        ) {
          this.setPagination(this.paginationSubject.value.limit);
        }
      }
    }

    if (component.settings?.localSortBy) {
      this.sortByColumn(
        component.settings?.localSortBy,
        <'asc' | 'desc'>component.settings?.localSortOrder,
      );
    }
  }

  public valuesReduce(row: TableDataValueRow) {
    return row.reduce(
      (acc, item, index: number) => {
        const acc2Index = this.dataTableColumnsMapped[index]?.id;
        acc[acc2Index] = item;
        return acc;
      },
      <Record<string | number, Dimension | { value: number; formatted: string }>>{},
    );
  }

  public changePage(page: number): void {
    const limit = this.paginationSubject.value.limit;

    this.paginationSubject.next({
      ...this.paginationSubject.value,
      page,
      start: (page - 1) * limit + 1,
      end:
        (page - 1) * limit +
        this.dataTableRowsMapped.slice((page - 1) * limit, (page - 1) * limit + limit).length,
    });
  }

  public toggleColumnSort(column: DataTableColumn): void {
    const nextSort: 'asc' | 'desc' = column.sort === 'desc' ? 'asc' : 'desc';
    this.tableSortChange.emit({
      ...column,
      sort: nextSort,
    });
  }

  public isCellClickable(value: number): boolean {
    return this.resourcesPreviewAvailable() && value > 0;
  }

  private sortByColumn(id: string, nextSort: 'asc' | 'desc') {
    if (!this.isExport()) {
      this.dataTableColumnsMapped = this.dataTableColumnsMapped.map(column =>
        column.id === id ? { ...column, sort: nextSort } : { ...column, sort: null },
      );
    }

    const sortSign = nextSort === 'asc' ? 1 : -1;

    const sort = (a: string | number, b: string | number) => (a > b ? 1 : -1) * sortSign;

    const emptySort = (id: string, a: ReportsDataTableRow, b: ReportsDataTableRow): number => {
      const aId = a.values[id].id;
      const bId = b.values[id].id;
      if (aId === EMPTY_VALUE || aId === NONE_VALUE) {
        return 1;
      }
      if (bId === EMPTY_VALUE || bId === NONE_VALUE) {
        return -1;
      }
      return 0;
    };

    this.dataTableRowsMapped = [...this.dataTableRowsMapped].sort((a, b) => {
      try {
        switch (id) {
          case 'conversion_rate': {
            const emptySortResult = emptySort(id, a, b);
            if (emptySortResult !== 0) {
              return emptySortResult;
            }
            const parsedA = parseInt(<string>a.values[id].value, 10);
            const parsedB = parseInt(<string>b.values[id].value, 10);
            return Number.isNaN(parsedB) ? sortSign : sort(parsedA, parsedB);
          }
          case 'stage-category':
          case 'stage-name': {
            const emptySortResult = emptySort(id, a, b);
            if (emptySortResult !== 0) {
              return emptySortResult;
            }
            return sort(<string>a.values[id].name, <string>b.values[id].name);
          }
          case 'count':
            return sort(<number>a.values[id]?.value, <number>b.values[id]?.value);

          default: {
            const emptySortResult = emptySort(id, a, b);
            if (emptySortResult !== 0) {
              return emptySortResult;
            }
            const aValue = <string | number>(
              (a.values[id]?.value ?? a.values[id]?.name ?? a.values[id]?.formatted)
            );
            const bValue = <string | number>(
              (b.values[id]?.value ?? b.values[id]?.name ?? b.values[id]?.formatted)
            );
            return sort(aValue, bValue);
          }
        }
      } catch (err) {
        console.error(`Could not sort by ${id}`);
        return 0;
      }
    });
  }

  private setPagination(limit: number) {
    const page = 1;

    this.paginationSubject.next({
      ...this.paginationSubject.value,
      page: page,
      pagesCount: Math.ceil(this.data?.vis.values.length / limit),
      start: (page - 1) * limit + 1,
      end:
        (page - 1) * limit +
        this.dataTableRowsMapped?.slice((page - 1) * limit, (page - 1) * limit + limit).length,
      total: this.data?.vis.values.length,
      limit: limit,
    });
  }

  private determineColumnSort(columnId: string | number): 'asc' | 'desc' | null | undefined {
    if (this.isExport()) {
      return null;
    }
    const component = this.component();
    if (component.sortBy && component.sortBy === columnId) {
      return component.sortOrder;
    }
    if (component.settings?.localSortBy && component.settings?.localSortBy === columnId) {
      return component.settings?.localSortOrder;
    }
    return null;
  }
}
